// ============================================================
// ProjectPortfolio — the V2 Projects surface (PHASE_2M P1 rebuild).
//
// The old surface was a 900-line static demo (__demo rows, 12 fake module
// tabs, setTimeout create). This rebuild lists ONLY real things and opens
// them like portals:
//
//   PROJECT PORTALS   — live sq_project_portals records (the Olivium Portal
//                       Factory spine, applied 2026-07-10). Expanding a row
//                       lazy-loads the REAL detail + context (modules /
//                       actions / blockers / receipts counts, repo +
//                       deployment, next action).
//   DIVISIONS         — the 9 division workspaces (organized doorways).
//
// "Create Project" routes to the Portal Factory create flow — the REAL
// persisted create path (audited + receipted). No local fake create remains.
//
// Portal-opening motif: expanding a row reveals a summary framed by an
// animated ring-trace gradient. Rows are <button>s — keyboard accessible
// (Enter/Space toggles, aria-expanded). Right-click opens the shared V2
// context menu; every action is a real handler or a labeled deferral.
// No emoji — SVG icons only.
// ============================================================

import { commandCenterPath } from '../commandCenterRoutes'
import { useCallback, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import CommandCenterShell, { CC_INK, CC_MONO } from '../shell/CommandCenterShell'
import { useContextMenu, type ContextMenuAction } from '../shell/ContextMenu'
import {
  PortalIcon, OpenIcon, PlusIcon, CopyIcon, ShareIcon, DownloadIcon,
  EmailIcon, TrashIcon, ScheduleIcon, PublishIcon, ChevronIcon,
} from '../shell/icons'
import {
  READINESS_META, portalDetailPath, portalContextPath, portalRepoPath, summarizePortal,
  type PortalRecord, type PortalDetail, type PortalContextRecord, type ReadinessStatus,
} from '../olivium/portal-factory/portalFactory'

export const PROJECTS_ROOT = commandCenterPath('projects')
export const PORTAL_FACTORY_CREATE = commandCenterPath('olivium/portal-factory/new')
export function projectDetailPath(projectId: string): string {
  return `${PROJECTS_ROOT}/${projectId}`
}

/** Lazy expansion payload — fetched from the LIVE backend by the route wrapper. */
export interface PortalSummaryBundle {
  detail: PortalDetail
  context: PortalContextRecord | null
}

export interface ProjectPortfolioProps {
  portals: PortalRecord[]
  loading?: boolean
  error?: string | null
  authRequired?: boolean
  onOpen?: (path: string) => void
  /** Live lazy loader for a portal row's expanded summary. */
  loadPortalSummary?: (id: string) => Promise<PortalSummaryBundle>
  onHome?: () => void
  onBackCommandCenter?: () => void
}

const rowBtn: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', padding: '13px 16px',
  borderRadius: 12, cursor: 'pointer', background: 'rgba(255,255,255,0.015)',
  border: '1px solid rgba(255,255,255,0.04)', color: CC_INK.primary, transition: 'border-color .2s, background .2s',
  fontFamily: 'inherit',
}

const enterBtn = (accent: string): CSSProperties => ({
  padding: '8px 16px', borderRadius: 9, cursor: 'pointer', background: `${accent}14`,
  border: `1px solid ${accent}38`, color: accent, fontSize: 11, fontFamily: CC_MONO, fontWeight: 800, letterSpacing: '0.06em',
})

function ReadinessChip({ status }: { status: ReadinessStatus }) {
  const meta = READINESS_META[status] || { label: status, accent: CC_INK.muted }
  return (
    <span style={{ fontSize: 8.5, fontWeight: 800, fontFamily: CC_MONO, letterSpacing: '0.1em', color: meta.accent, padding: '2px 8px', borderRadius: 5, background: `${meta.accent}12`, border: `1px solid ${meta.accent}30`, whiteSpace: 'nowrap' }}>
      {meta.label.toUpperCase()}
    </span>
  )
}

/** The portal-opening frame — animated ring-trace border around expanded content. */
export function PortalFrame({ accent, children }: { accent: string; children: ReactNode }) {
  return (
    <div style={{
      padding: 1.5, borderRadius: 14, margin: '6px 2px 10px',
      background: `linear-gradient(90deg, ${accent}55, ${accent}, ${accent}44, ${accent}, ${accent}55)`,
      backgroundSize: '300% 100%', animation: 'sqv2RingTrace 6s linear infinite',
      boxShadow: `0 0 14px ${accent}1f`,
    }}>
      <div style={{ padding: '16px 18px', borderRadius: 13, background: '#0b0c12', animation: 'sqv2PortalOpen .3s ease' }}>
        {children}
      </div>
    </div>
  )
}

function Stat({ label, value, accent }: { label: string; value: string | number; accent?: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5, marginRight: 14 }}>
      <span style={{ fontSize: 14, fontWeight: 800, color: accent || CC_INK.primary, fontFamily: CC_MONO }}>{value}</span>
      <span style={{ fontSize: 9.5, color: CC_INK.faint, letterSpacing: '0.06em', fontFamily: CC_MONO }}>{label.toUpperCase()}</span>
    </span>
  )
}

// ---- portal row (live record) ----------------------------------------------

function PortalRow({ portal, onOpen, loadPortalSummary, menu }: {
  portal: PortalRecord
  onOpen?: (path: string) => void
  loadPortalSummary?: (id: string) => Promise<PortalSummaryBundle>
  menu: ReturnType<typeof useContextMenu>
}) {
  const [open, setOpen] = useState(false)
  const [bundle, setBundle] = useState<PortalSummaryBundle | null>(null)
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'failed'>('idle')
  const accent = '#7c3aed'
  const detailPath = portalDetailPath(portal.id)

  const toggle = useCallback(() => {
    const next = !open
    setOpen(next)
    if (next && !bundle && loadPortalSummary && loadState !== 'loading') {
      setLoadState('loading')
      loadPortalSummary(portal.id)
        .then((b) => { setBundle(b); setLoadState('idle') })
        .catch(() => setLoadState('failed'))
    }
  }, [open, bundle, loadPortalSummary, loadState, portal.id])

  const appUrl = typeof window !== 'undefined' ? `${window.location.origin}${detailPath}` : detailPath
  const copyLink = () => { void navigator.clipboard?.writeText(appUrl) }
  const exportJson = () => {
    const payload = JSON.stringify(bundle?.detail ?? portal, null, 2)
    const blob = new Blob([payload], { type: 'application/json' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `portal-${portal.project_slug}.json`
    a.click()
    URL.revokeObjectURL(a.href)
  }
  const actions: ContextMenuAction[] = [
    { id: 'open', label: 'Open', icon: <OpenIcon size={13} />, onSelect: () => onOpen?.(detailPath) },
    { id: 'copy', label: 'Copy Link', icon: <CopyIcon size={13} />, onSelect: copyLink, hint: 'Copies the portal route URL' },
    { id: 'share', label: 'Share', icon: <ShareIcon size={13} />, onSelect: copyLink, hint: 'Copies the link to share — no external send' },
    { id: 'email', label: 'Email', icon: <EmailIcon size={13} still />, onSelect: () => { window.location.href = `mailto:?subject=${encodeURIComponent(`Project portal — ${portal.project_name}`)}&body=${encodeURIComponent(appUrl)}` }, hint: 'Opens your mail client' },
    { id: 'download', label: 'Download JSON', icon: <DownloadIcon size={13} />, onSelect: exportJson, hint: 'Real export of the saved record' },
    { id: 'publish', label: 'Publish to Portal', icon: <PublishIcon size={13} />, onSelect: () => onOpen?.(portalContextPath(portal.id)), hint: 'Opens the client-access bridge (publish lives there)' },
    { id: 'schedule', label: 'Schedule', icon: <ScheduleIcon size={13} />, deferred: 'INTEGRATION_DEFERRED' },
    { id: 'delete', label: 'Delete', icon: <TrashIcon size={13} />, danger: true, deferred: 'BACKEND_DEFERRED', hint: 'No portal delete API yet — never faked' },
  ]

  const summary = bundle ? summarizePortal(bundle.detail) : null
  return (
    <div>
      <button
        type="button"
        style={{ ...rowBtn, borderColor: open ? `${accent}44` : 'rgba(255,255,255,0.04)' }}
        onClick={toggle}
        onContextMenu={(e) => menu.open(e, actions, `Portal — ${portal.project_name}`)}
        aria-expanded={open}
      >
        <PortalIcon size={17} color={accent} still={!open} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{portal.project_name}</span>
          <span style={{ display: 'block', fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO, marginTop: 2 }}>
            {portal.project_type.replace(/_/g, ' ')} · {portal.project_slug}
          </span>
        </span>
        {portal.owner_label ? (
          <span className="sqv2-proj-owner" style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO, whiteSpace: 'nowrap' }}>{portal.owner_label}</span>
        ) : null}
        {portal.counts ? (
          <span className="sqv2-proj-mini" style={{ fontSize: 9.5, color: CC_INK.muted, fontFamily: CC_MONO, whiteSpace: 'nowrap' }}>
            {portal.counts.open_actions}a · <span style={{ color: portal.counts.open_blockers > 0 ? '#dc2626' : CC_INK.muted }}>{portal.counts.open_blockers}b</span>
          </span>
        ) : null}
        <ReadinessChip status={portal.readiness_status} />
        <ChevronIcon size={13} color={CC_INK.faint} direction={open ? 'down' : 'right'} />
      </button>

      {open && (
        <PortalFrame accent={accent}>
          {loadState === 'loading' && <p style={{ margin: 0, fontSize: 11.5, color: CC_INK.faint, fontStyle: 'italic' }}>Opening portal — loading the live record…</p>}
          {loadState === 'failed' && <p style={{ margin: 0, fontSize: 11.5, color: '#f87171' }}>Could not load the live portal detail — the row above is still the real saved record.</p>}
          {portal.summary && <p style={{ margin: '0 0 10px', fontSize: 12.5, color: CC_INK.secondary, lineHeight: 1.6 }}>{portal.summary}</p>}
          <div style={{ marginBottom: 12 }}>
            {summary ? (
              <>
                <Stat label="modules" value={summary.modules} accent="#7c3aed" />
                <Stat label="open actions" value={summary.openActions} accent="#0891b2" />
                <Stat label="open blockers" value={summary.openBlockers} accent={summary.openBlockers > 0 ? '#dc2626' : undefined} />
                <Stat label="receipts" value={summary.receipts} accent="#059669" />
              </>
            ) : portal.counts ? (
              <>
                <Stat label="modules" value={portal.counts.modules} accent="#7c3aed" />
                <Stat label="open actions" value={portal.counts.open_actions} accent="#0891b2" />
                <Stat label="open blockers" value={portal.counts.open_blockers} accent={portal.counts.open_blockers > 0 ? '#dc2626' : undefined} />
                <Stat label="receipts" value={portal.counts.receipts} accent="#059669" />
              </>
            ) : null}
          </div>
          <div style={{ fontSize: 10.5, fontFamily: CC_MONO, color: CC_INK.faint, marginBottom: 12, display: 'grid', gap: 3 }}>
            <span>portal url · {detailPath}</span>
            {bundle?.context?.repo_url && <span>repo · {bundle.context.repo_url}{bundle.context.active_branch ? ` @ ${bundle.context.active_branch}` : ''}</span>}
            {bundle?.context?.deployment_url && <span>deploy · {bundle.context.deployment_url}</span>}
            {bundle?.context?.next_action && <span style={{ color: '#b8862a' }}>next · {bundle.context.next_action}</span>}
            {bundle && !bundle.context && <span>no repo/context saved yet — add it from the portal</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" style={enterBtn(accent)} onClick={() => onOpen?.(detailPath)}>ENTER PROJECT</button>
            <button type="button" style={enterBtn('#0891b2')} onClick={() => onOpen?.(portalContextPath(portal.id))}>CONTEXT</button>
            <button type="button" style={enterBtn('#059669')} onClick={() => onOpen?.(portalRepoPath(portal.id))}>REPO</button>
          </div>
        </PortalFrame>
      )}
    </div>
  )
}

// ---- status grouping (derived from REAL readiness_status — never fabricated) --

export type ProjectGroupKey = 'action_required' | 'active' | 'draft' | 'archived'

export const PROJECT_GROUPS: Array<{ key: ProjectGroupKey; label: string; hint: string; accent: string }> = [
  { key: 'action_required', label: 'Action Required', hint: 'a blocker or a pending review needs you', accent: '#dc2626' },
  { key: 'active', label: 'Active Projects', hint: 'currently in motion', accent: '#059669' },
  { key: 'draft', label: 'Draft Projects', hint: 'planning — not started yet', accent: '#8a94a0' },
  { key: 'archived', label: 'Archived Projects', hint: 'closed out', accent: '#6a7480' },
]

// Map a live portal's REAL readiness_status onto a portfolio group. Paused and
// Completed are intentionally absent — the readiness vocabulary has no such value,
// so those sections are never rendered (no empty shells, no fabricated status).
export function portalGroupKey(p: PortalRecord): ProjectGroupKey {
  if (p.readiness_status === 'blocked' || p.readiness_status === 'in_review') return 'action_required'
  if ((p.counts?.open_blockers ?? 0) > 0) return 'action_required'
  if (p.readiness_status === 'planning') return 'draft'
  if (p.readiness_status === 'archived') return 'archived'
  return 'active' // building | ready | live
}

// ---- the surface ------------------------------------------------------------

export default function ProjectPortfolio({
  portals, loading, error, authRequired, onOpen, loadPortalSummary, onHome, onBackCommandCenter,
}: ProjectPortfolioProps) {
  const menu = useContextMenu()

  // Search + status filter live in the URL (?q= / ?status=) so browser Back and a
  // shared/bookmarked link restore the exact portfolio view. Invalid status → All.
  const [params, setParams] = useSearchParams()
  const search = params.get('q') ?? ''
  const rawStatus = params.get('status')
  const filter: ProjectGroupKey | 'all' = PROJECT_GROUPS.some((g) => g.key === rawStatus) ? (rawStatus as ProjectGroupKey) : 'all'
  const setSearch = (v: string) => {
    const next = new URLSearchParams(params)
    if (v) next.set('q', v); else next.delete('q')
    setParams(next, { replace: true })
  }
  const setFilter = (k: ProjectGroupKey | 'all') => {
    const next = new URLSearchParams(params)
    if (k === 'all') next.delete('status'); else next.set('status', k)
    setParams(next, { replace: true })
  }

  const q = search.trim().toLowerCase()
  const matchPortal = (p: PortalRecord) =>
    !q || p.project_name.toLowerCase().includes(q) || p.project_slug.toLowerCase().includes(q) || p.project_type.toLowerCase().includes(q)

  const visiblePortals = portals.filter(matchPortal)

  // Group by REAL derived status. Registered client projects are active projects.
  const grouped: Record<ProjectGroupKey, { portals: PortalRecord[] }> = {
    action_required: { portals: [] },
    active: { portals: [] },
    draft: { portals: [] },
    archived: { portals: [] },
  }
  for (const p of visiblePortals) grouped[portalGroupKey(p)].portals.push(p)
  for (const key of Object.keys(grouped) as ProjectGroupKey[]) {
    grouped[key].portals.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || '')) // most recent first
  }
  const groupCount = (k: ProjectGroupKey) => grouped[k].portals.length

  const totalVisible = visiblePortals.length
  const totalAll = portals.length
  const populatedGroups = PROJECT_GROUPS.filter((g) => groupCount(g.key) > 0)

  const sectionTitle = (label: string, hint: string, count: number, accent: string): ReactNode => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '20px 2px 8px' }}>
      <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', color: accent }}>{label}</span>
      <span style={{ fontSize: 10, fontWeight: 800, fontFamily: CC_MONO, color: CC_INK.faint, padding: '1px 7px', borderRadius: 20, background: 'rgba(255,255,255,0.05)' }}>{count}</span>
      <span style={{ fontSize: 10.5, color: CC_INK.whisper }}>{hint}</span>
    </div>
  )

  const filterChip = (key: ProjectGroupKey | 'all', label: string, count: number, accent: string) => {
    const on = filter === key
    return (
      <button
        key={key}
        type="button"
        onClick={() => setFilter(key)}
        aria-pressed={on}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20, cursor: 'pointer',
          fontSize: 11, fontWeight: 700, fontFamily: 'inherit',
          background: on ? `${accent}1f` : 'rgba(255,255,255,0.02)',
          border: `1px solid ${on ? `${accent}55` : 'rgba(255,255,255,0.06)'}`,
          color: on ? accent : CC_INK.muted,
        }}
      >
        {label}
        <span style={{ fontSize: 9.5, fontFamily: CC_MONO, opacity: 0.8 }}>{count}</span>
      </button>
    )
  }

  return (
    <CommandCenterShell
      onHome={onHome}
      onBackCommandCenter={onBackCommandCenter}
      breadcrumb={['COMMAND CENTER', 'PROJECTS']}
      title="Projects"
      titleAccent="#7c3aed"
      honestyBadge={{
        label: 'PORTFOLIO · LIVE RECORDS',
        title: 'Project portals are real sq_project_portals rows loaded from the live backend; expanding a row loads its real detail + context. Projects are grouped by their real readiness status — nothing here is a demo row, and divisions are not projects.',
      }}
      contextBar={
        <>
          <span style={{ fontSize: 12, color: CC_INK.muted }}>Every project and workspace currently managed through Square Code.</span>
          <span style={{ flex: 1 }} />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search projects"
            aria-label="Search projects"
            style={{ width: 200, maxWidth: '40vw', padding: '8px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: CC_INK.primary, fontSize: 12 }}
          />
          <button
            type="button"
            onClick={() => onOpen?.(PORTAL_FACTORY_CREATE)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 10, cursor: 'pointer', background: 'linear-gradient(135deg, rgba(124,58,237,0.3), rgba(124,58,237,0.16))', border: '1px solid rgba(124,58,237,0.4)', color: '#a78bfa', fontSize: 12, fontWeight: 800 }}
          >
            <PlusIcon size={13} /> Create Project
          </button>
        </>
      }
    >
      <style>{`
        @keyframes sqv2RingTrace { 0% { background-position: 0% 0; } 100% { background-position: 300% 0; } }
        @keyframes sqv2PortalOpen { from { opacity: 0; transform: translateY(-5px) scale(0.995); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @media (max-width: 680px) { .sqv2-proj-owner, .sqv2-proj-mini { display: none !important; } }
      `}</style>

      <div style={{ width: '100%', maxWidth: 'min(1320px, 100%)', borderRadius: 18, border: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.012)', padding: '16px 18px 24px', maxHeight: 'calc(100vh - 210px)', overflowY: 'auto' }}>
        {/* Live-fetch honesty states */}
        {loading && <p style={{ fontSize: 11.5, color: CC_INK.faint, fontStyle: 'italic', padding: '4px 2px' }}>Loading the live portal registry…</p>}
        {authRequired && <p style={{ fontSize: 11.5, color: CC_INK.faint, padding: '4px 2px' }}>Sign in to the live cockpit to load your project portals.</p>}
        {error && <p style={{ fontSize: 11.5, color: '#f87171', padding: '4px 2px' }}>{error}</p>}

        {/* Status filter — only offers groups that actually contain real projects */}
        {!loading && !authRequired && !error && populatedGroups.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '2px 2px 4px' }}>
            {filterChip('all', 'All', totalVisible, '#a78bfa')}
            {populatedGroups.map((g) => filterChip(g.key, g.label, groupCount(g.key), g.accent))}
          </div>
        )}

        {/* Whole-page empty state (no projects at all) */}
        {!loading && !authRequired && !error && totalAll === 0 && (
          <p style={{ fontSize: 12, color: CC_INK.faint, padding: '14px 2px' }}>
            No projects have been created yet. Create the first project to begin organizing work.
          </p>
        )}

        {/* Search empty state */}
        {!loading && !authRequired && !error && totalAll > 0 && totalVisible === 0 && (
          <p style={{ fontSize: 12, color: CC_INK.faint, padding: '14px 2px' }}>
            No projects match “{search.trim()}”. Clear the search to see the full portfolio.
          </p>
        )}

        {/* Grouped portfolio — only real, non-empty status sections render */}
        {PROJECT_GROUPS.map((g) => {
          if (filter !== 'all' && filter !== g.key) return null
          const bucket = grouped[g.key]
          if (bucket.portals.length === 0) return null
          return (
            <div key={g.key}>
              {sectionTitle(g.label, g.hint, groupCount(g.key), g.accent)}
              <div style={{ display: 'grid', gap: 6 }}>
                {bucket.portals.map((p) => (
                  <PortalRow key={p.id} portal={p} onOpen={onOpen} loadPortalSummary={loadPortalSummary} menu={menu} />
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {menu.element}
    </CommandCenterShell>
  )
}
